/**
 * useTendrilFeedbackDeleteMutation — DELETE /api/tendril/feedback.
 *
 * Removes the single feedback row for a (conversationId, turnIndex) when the
 * user clicks the currently-selected thumb again to un-rate the turn (see
 * `handleFeedbackDelete()` in backend/src/handlers/tendril.ts). Feedback is
 * idempotent per turn (SK = FEEDBACK#{turnIndex}), so a delete of a
 * never-written / already-removed row is a no-op success.
 *
 * `apiClient.delete` sends no request body, so the identifiers travel as
 * query-string params. Mirrors useTendrilFeedbackMutation's react-query shape.
 */
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '../../api/client.js';

export interface TendrilFeedbackDeleteVars {
  conversationId: string;
  turnIndex: number;
}

export interface TendrilFeedbackDeleteResponse {
  success: boolean;
}

export function useTendrilFeedbackDeleteMutation(): UseMutationResult<
  TendrilFeedbackDeleteResponse,
  Error,
  TendrilFeedbackDeleteVars
> {
  return useMutation<TendrilFeedbackDeleteResponse, Error, TendrilFeedbackDeleteVars>({
    mutationFn: async ({ conversationId, turnIndex }) => {
      const query = new URLSearchParams({
        conversationId,
        turnIndex: String(turnIndex),
      }).toString();
      const raw = await apiClient.delete(`/api/tendril/feedback?${query}`);
      return raw as TendrilFeedbackDeleteResponse;
    },
  });
}
